import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, X, FileText, MessageSquare, Image as ImageIcon, HandHeart } from "lucide-react";

const KINDS = [["post", "Post", MessageSquare], ["photo", "Photo", ImageIcon], ["article", "Article", FileText], ["prayer", "Prayer request", HandHeart]];

export default function Composer({ orgId, onPosted }) {
  const [kind, setKind] = useState("post");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [visibility, setVisibility] = useState("church");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    for (const f of Array.from(files).slice(0, 10 - images.length)) {
      const fd = new FormData(); fd.append("file", f);
      try { const r = await api.post("/social/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setImages((x) => [...x, r.data]); }
      catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/social/posts", { org_id: orgId, kind, title, body, image_ids: images.map((i) => i.id), visibility });
      setTitle(""); setBody(""); setImages([]); setKind("post");
      onPosted(r.data);
      toast.success("Posted.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Couldn't post.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl p-4" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="social-composer">
      <div className="flex gap-1 mb-3">
        {KINDS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => { setKind(k); if (k === "photo") fileRef.current?.click(); }} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors" style={{ background: kind === k ? "var(--gold)" : "transparent", color: kind === k ? "var(--bg-base)" : "var(--ink-2)" }} data-testid={`composer-kind-${k}`}><Icon size={13} /> {l}</button>
        ))}
      </div>
      {kind === "article" && <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Article title" className="w-full px-3 py-2 mb-2 text-base font-serif-display" data-testid="composer-title" />}
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={kind === "article" ? 8 : 3} placeholder={kind === "article" ? "Write your article…" : kind === "prayer" ? "What can we pray with you about?" : "Share something with your church…"} className="w-full px-3 py-2 text-sm resize-y" data-testid="composer-body" />
      {images.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {images.map((i) => (
            <div key={i.id} className="relative">
              <img src={`${API.replace(/\/api$/, "")}${i.url}`} alt="" className="w-20 h-20 object-cover rounded-md" />
              <button onClick={() => setImages((x) => x.filter((y) => y.id !== i.id))} className="absolute -top-1.5 -right-1.5 rounded-full p-0.5" style={{ background: "var(--bg-deep)", color: "var(--ink)" }}><X size={11} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--gold)" }} title="Add photos" data-testid="composer-add-photo"><ImagePlus size={18} /></button>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="text-xs px-2 py-1.5" data-testid="composer-visibility">
            <option value="church">My church only</option>
            <option value="public">Public: my church + Network</option>
          </select>
        </div>
        <button onClick={submit} disabled={busy || (!body.trim() && !images.length)} className="btn-primary" style={{ padding: "0.5rem 1.2rem" }} data-testid="composer-submit">{busy ? "Posting…" : "Post"}</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="composer-file-input" />
    </div>
  );
}
